import { Capacitor } from '@capacitor/core';
import { motion, AnimatePresence } from 'framer-motion';
import React, { useCallback, useState, useEffect, useRef, useLayoutEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import TourProvider from '../../../components/AppTour/TourProvider';
import LoadingSpinner from '../../../components/LoadingSpinner';
import {
  MobileLayout,
  MobileProfileMenu,
  MobileHomeDashboard,
  MobileNativeHeader,
  MobilePageSkeleton,
} from '../../../components/mobile';
import OnboardingFlow from '../../../components/Onboarding/OnboardingFlow';
import SessionTimeoutModal from '../../../components/SessionTimeoutModal';
import { usePageTransition } from '../../../hooks/usePageTransition';
import { TOUR_NAVIGATE_EVENT, type TourNavigateDetail } from '../../../lib/tourNavigation';
import { useSessionTimeout } from '../../../hooks/useSessionTimeout';
import { resolveSessionConfig } from '../../../shared/config/sessionConfig';
import PaymentHistoryPage from '../../../pages/PaymentHistoryPage';
import ProfilePage from '../../../pages/ProfilePage';
import AccountSettingsPage from '../../../pages/AccountSettingsPage';
import { useUserPreferences } from '../../../shared/hooks/useUserPreferences';
import { slideTransitionVariants } from '../../../shared/utils/microAnimations';
import { isNativePlatform } from '../../../utils/capacitorUtils';



// Import dashboard components
import AgentHubPage from '../../ai-agent/AgentHubPage';
// SingleOptimizeFlow import removed — the single-optimize page is deprecated
// and now redirects to /agent. The original component lives in deadcode at
// src/_deadcode/SingleOptimizeFlow/ for reference.
import Collaborate from './Collaborate';
import DashboardOverview from './DashboardOverview';
import DeveloperSettings from './DeveloperSettings';
import JourneySetupPage from './JourneySetupPage';
import Jobs from './Jobs';
import Layout from './Layout';
import SkillAssessment from './SkillAssessment';
import ObjectivesTasksPage from './ObjectivesTasks/ObjectivesTasksPage';
import MyActivities from './MyActivities';
import OptimizationHistory from './OptimizationHistory';
import PrepareForInterview from './PrepareForInterview';
import ResumeBuilderView from './ResumeBuilder/ResumeBuilderView';
import Subscription from './Subscription';
import ProductFeedbackPage from './ProductFeedbackPage';
import SupportTickets from './SupportTickets';
import UserProfile from './UserProfile';
import BlogListPage from '../../../pages/admin/BlogListPage';
import BlogEditorPage from '../../../pages/admin/BlogEditorPage';
import CareersListPage from '../../../pages/admin/CareersListPage';
import CareersEditorPage from '../../../pages/admin/CareersEditorPage';
import ApplyConfirmationModal, { type ApplyChoice } from './ApplyConfirmationModal';
import type { Job } from './JobCard';

export type DashboardPage = 
  | 'dashboard'
  | 'journey-setup'
  | 'single-optimize'
  | 'history' 
  | 'resume-builder'
  | 'payment-history'
  | 'my-activities'
  | 'prepare-for-interview'
  | 'jobs'
  | 'jobs-admin'
  | 'objectives-tasks'
  | 'collaborate'
  | 'ai-companion'
  | 'agent'
  | 'calendar-notes'
  | 'support'
  | 'product-feedback'
  | 'profile'
  | 'profile-info'
  | 'credits'
  | 'developer'
  | 'account-settings'
  | 'skill-assessment'
  | 'blog-cms'
  | 'blog-editor'
  | 'careers-cms'
  | 'careers-editor';

interface DashboardContainerProps {
  onSignOut: () => void | Promise<void>;
}

/* Human-readable labels for each page — used in MobileNativeHeader */
const SCREEN_LABELS: Record<DashboardPage, string> = {
  dashboard:               'Home',
  'journey-setup':         'Journey Setup',
  'single-optimize':       'Single Optimize',
  history:                 'My Activities',
  'resume-builder':        'Resume Builder',
  'payment-history':       'Payment History',
  'my-activities':         'My Activities',
  'prepare-for-interview': 'Prepare for Interview',
  jobs:                    'Jobs',
  'jobs-admin':            'Job Board',
  'objectives-tasks':      'Objectives',
  collaborate:             'Collaborate',
  'ai-companion':          'AI Companion',
  agent:                   'AI Command Center',
  'calendar-notes':        'Calendar & Notes',
  support:                 'Help & Support',
  'product-feedback':      'Product Feedback',
  profile:                 'My Account',
  'profile-info':          'Profile Information',
  credits:                 'Subscription',
  developer:               'API Keys',
  'skill-assessment':      'Skill Assessment',
  'blog-cms':              'Blog Posts',
  'blog-editor':           'Blog Editor',
  'careers-cms':           'Careers CMS',
  'careers-editor':        'Career Editor',
};

const DashboardContainer: React.FC<DashboardContainerProps> = ({ onSignOut }) => {
  const { t } = useTranslation();
  const { preferences, loading: preferencesLoading } = useUserPreferences();
  const { currentPage, triggerTransition } = usePageTransition('agent');

  /** Hero strip search stays on Jobs — optimize flow routes to Agent from job CTAs. */
  const handleHeroSearch = useCallback((q: string) => {
    const t = q.trim();
    if (!t) return;
    window.history.pushState({}, '', `/jobs?q=${encodeURIComponent(t)}`);
    window.dispatchEvent(new PopStateEvent('popstate'));
  }, []);
  const [showOnboarding, setShowOnboarding] = useState(false);

  // ── WO-17 PR-C: Apply-confirmation modal ──────────────────────────────────
  const [pendingApply, setPendingApply] = useState<{
    clickId: string | null;
    job: Job;
  } | null>(null);
  // Keeps track of clickIds we've already shown the modal for — resets on reload.
  const dismissedClickIds = useRef<Set<string>>(new Set());

  /* ── Native skeleton loading ──────────────────────────────────────
   * Show a skeleton screen for ~400 ms whenever the page changes on
   * native. This replaces loading spinners inside web components and
   * makes every transition feel instant.                             */
  const isNative = Capacitor.isNativePlatform();
  const prevPageRef = useRef<DashboardPage>(currentPage);
  const [showSkeleton, setShowSkeleton] = useState(false);

  /* ── Native navigation stack ───────────────────────────────────────
   * Tracks where the user navigated FROM so the back button returns to
   * the actual previous screen instead of always going to dashboard.   */
  const [navStack, setNavStack] = useState<DashboardPage[]>([]);

  /* Pages that have dedicated native components or render fast enough
   * that a skeleton would feel like an unnecessary flash.            */
  const NO_SKELETON_PAGES: DashboardPage[] = ['dashboard', 'profile', 'profile-info', 'credits'];

  useEffect(() => {
    if (!isNative) return;
    if (currentPage === prevPageRef.current) return;
    prevPageRef.current = currentPage;

    if (NO_SKELETON_PAGES.includes(currentPage)) {
      // Always clear any lingering skeleton immediately when landing on these pages
      setShowSkeleton(false);
      return;
    }
    setShowSkeleton(true);
    const t = setTimeout(() => setShowSkeleton(false), 300);
    return () => clearTimeout(t);
  }, [currentPage, isNative]); // eslint-disable-line react-hooks/exhaustive-deps

  // Session timeout management — env defaults merged with user DB preferences
  const sessionConfig = useMemo(
    () => resolveSessionConfig(
      (preferences as { session_preferences?: {
        timeout_enabled?: boolean;
        timeout_minutes?: number;
        warning_minutes?: number;
      } } | null)?.session_preferences,
    ),
    [preferences],
  );
  const {
    isWarningVisible,
    timeRemaining,
    extendSession,
    signOutNow
  } = useSessionTimeout(sessionConfig);

  // Set document title by current page (i18n-aware)
  const pageTitleKeys: Record<DashboardPage, string> = {
    dashboard: 'dashboard.pageTitles.dashboard',
    'single-optimize': 'dashboard.pageTitles.single-optimize',
    history: 'dashboard.pageTitles.history',
    'resume-builder': 'dashboard.pageTitles.resume-builder',
    'payment-history': 'dashboard.pageTitles.payment-history',
    'prepare-for-interview': 'dashboard.pageTitles.prepare-for-interview',
    jobs: 'dashboard.pageTitles.jobs',
    'jobs-admin': 'dashboard.pageTitles.jobsAdmin',
    'objectives-tasks': 'dashboard.pageTitles.objectives-tasks',
    collaborate: 'dashboard.pageTitles.collaborate',
    'ai-companion': 'dashboard.pageTitles.ai-companion',
    'calendar-notes': 'dashboard.pageTitles.calendar-notes',
    support: 'dashboard.pageTitles.support',
    profile: 'dashboard.pageTitles.profile',
    'profile-info': 'dashboard.pageTitles.profile',
    credits: 'dashboard.pageTitles.credits',
    developer: 'dashboard.pageTitles.developer',
    'skill-assessment': 'dashboard.pageTitles.skill-assessment',
    'journey-setup': 'dashboard.pageTitles.journey-setup',
    'blog-cms': 'dashboard.pageTitles.blog-cms',
    'blog-editor': 'dashboard.pageTitles.blog-editor',
    'careers-cms': 'dashboard.pageTitles.careers-cms',
    'careers-editor': 'dashboard.pageTitles.careers-cms',
    agent: 'dashboard.pageTitles.agent',
  };
  useEffect(() => {
    const base = 'GoGrabJob';
    const key = pageTitleKeys[currentPage];
    const fallback = SCREEN_LABELS[currentPage] ?? '';
    const pageTitle = key ? t(key, fallback) : fallback;
    document.title = pageTitle ? `${pageTitle} - ${base}` : base;
  }, [currentPage, t]);

  // When arriving from optimize flow with a chosen next action, open that page
  useEffect(() => {
    try {
      const openPage = sessionStorage.getItem('dashboard_open_page');
      if (openPage && ['prepare-for-interview', 'collaborate', 'dashboard', 'history', 'resume-builder', 'objectives-tasks', 'jobs'].includes(openPage)) {
        sessionStorage.removeItem('dashboard_open_page');
        triggerTransition(openPage);
      }
    } catch {
      // ignore
    }
  }, [triggerTransition]);

  // SECURITY: Check for mandatory password reset on dashboard access
  useEffect(() => {
    const mandatoryReset = sessionStorage.getItem('mandatory_password_reset');
    if (mandatoryReset === 'true') {
      window.location.href = '/reset-password';
    }
  }, []);

  // Check if user needs onboarding
  useEffect(() => {
    if (!preferencesLoading && preferences) {
      // IMPORTANT: Check localStorage first for immediate feedback
      const onboardingDisabled = localStorage.getItem('onboarding_permanently_disabled') === 'true';
      
      if (onboardingDisabled) {
        console.log('✅ Onboarding permanently disabled (localStorage)');
        setShowOnboarding(false);
        sessionStorage.removeItem('trigger_onboarding');
        sessionStorage.removeItem('manual_onboarding_trigger');
        return;
      }

      // Check if user just completed payment - skip onboarding in this case
      const paymentSuccessData = localStorage.getItem('paymentSuccess');
      let skipDueToPayment = false;
      
      if (paymentSuccessData) {
        try {
          const paymentData = JSON.parse(paymentSuccessData);
          skipDueToPayment = paymentData.skipOnboarding === true;
          console.log('💰 Payment success detected:', paymentData);
          
          // Dispatch refresh event BEFORE clearing localStorage so PlanStatusProvider schedules its staggered refreshes
          window.dispatchEvent(new CustomEvent('planStatusRefresh', { detail: paymentData }));
          
          // Clear the flag after dispatching
          if (skipDueToPayment) {
            localStorage.removeItem('paymentSuccess');
          }
        } catch (e) {
          console.error('Error parsing payment success data:', e);
        }
      }
      
      // Show full dashboard/journey by default; onboarding modal only on explicit request (Profile > Setup).
      const manualTrigger = sessionStorage.getItem('manual_onboarding_trigger') === 'true';
      const shouldShowOnboarding = !onboardingDisabled && !skipDueToPayment && manualTrigger;

      if (shouldShowOnboarding) {
        setShowOnboarding(true);
      } else {
        sessionStorage.removeItem('trigger_onboarding');
        sessionStorage.removeItem('manual_onboarding_trigger');
      }
    }
  }, [preferences, preferencesLoading]);

  // Listen for manual onboarding trigger event
  useEffect(() => {
    const handleShowOnboarding = () => {
      console.log('?? Manual onboarding triggered via event');
      setShowOnboarding(true);
    };

    window.addEventListener('showOnboarding', handleShowOnboarding);
    return () => window.removeEventListener('showOnboarding', handleShowOnboarding);
  }, []);

  // Handle onboarding completion
  const handleOnboardingComplete = () => {
    console.log('?? Onboarding completed, hiding modal');
    setShowOnboarding(false);
    sessionStorage.removeItem('manual_onboarding_trigger');
    sessionStorage.removeItem('trigger_onboarding');
  };

  // ── WO-17 PR-C: focus listener — show apply-confirmation after tab return ──
  useEffect(() => {
    const DELAY_MS = 20_000; // 20 s after click before the modal is eligible

    const handleFocus = () => {
      try {
        const raw = sessionStorage.getItem('pendingApplyConfirm');
        if (!raw) return;

        const parsed = JSON.parse(raw) as {
          clickId: string | null;
          job: Job;
          ts: number;
        };

        if (Date.now() - parsed.ts < DELAY_MS) return;

        // Dedupe: never show the same clickId twice
        if (parsed.clickId && dismissedClickIds.current.has(parsed.clickId)) {
          sessionStorage.removeItem('pendingApplyConfirm');
          return;
        }

        setPendingApply({ clickId: parsed.clickId, job: parsed.job });
      } catch {
        // ignore malformed storage entries
      }
    };

    window.addEventListener('focus', handleFocus);
    return () => window.removeEventListener('focus', handleFocus);
  }, []);

  const handleModalClose = useCallback((choice: ApplyChoice) => {
    if (pendingApply?.clickId) {
      dismissedClickIds.current.add(pendingApply.clickId);
    }
    setPendingApply(null);
    // sessionStorage.removeItem already called inside the modal on every choice
    void choice; // choice available here for future analytics
  }, [pendingApply]);

  // Handle page navigation — push current page onto native nav stack
  const handleNavigate = useCallback((page: DashboardPage) => {
    if (isNative) {
      setNavStack(prev => [...prev, currentPage]);
    }
    triggerTransition(page, 'forward');
  }, [isNative, currentPage, triggerTransition]);

  // Product tour: sync React page state when tour navigates via URL.
  useEffect(() => {
    const onTourNav = (e: Event) => {
      const { page } = (e as CustomEvent<TourNavigateDetail>).detail;
      if (page) triggerTransition(page, 'forward');
    };
    window.addEventListener(TOUR_NAVIGATE_EVENT, onTourNav);
    return () => window.removeEventListener(TOUR_NAVIGATE_EVENT, onTourNav);
  }, [triggerTransition]);

  // Handle back navigation — pop the nav stack on native
  const handleBack = () => {
    if (isNative && navStack.length > 0) {
      const prev = navStack[navStack.length - 1];
      setNavStack(stack => stack.slice(0, -1));
      triggerTransition(prev, 'back');
    } else {
      triggerTransition('dashboard', 'back');
    }
  };

  // Listen for Capacitor hardware back button (Android) — dispatched by main.tsx
  useEffect(() => {
    if (!isNative) return;
    const onCapacitorBack = () => { handleBack(); };
    window.addEventListener('capacitor-back-button', onCapacitorBack);
    return () => window.removeEventListener('capacitor-back-button', onCapacitorBack);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isNative, navStack, currentPage]);

  /**
   * NativeScreenContent — scrollable content area that uses useLayoutEffect
   * to imperatively hide web-style page title blocks after every render.
   * This is more reliable than CSS because it runs after the DOM is painted,
   * regardless of CSS specificity or build-time purging.
   */
  const NativeScreenContent: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const ref = useRef<HTMLDivElement>(null);
    useLayoutEffect(() => {
      const el = ref.current;
      if (!el) return;
      // Hide every web-style h1 page title and its containing title row
      el.querySelectorAll<HTMLElement>('h1.ggj-page-title').forEach(h1 => {
        h1.style.display = 'none';
        // Also collapse the subtitle p directly after the h1
        const next = h1.nextElementSibling as HTMLElement | null;
        if (next?.tagName === 'P') next.style.display = 'none';
        // Collapse the entire title block wrapper (flex-shrink-0 row)
        const block = h1.closest<HTMLElement>('[class*="flex-shrink-0"]');
        if (block && block !== el) block.style.display = 'none';
      });
      // Strip the big top padding web components add for their own title area
      const firstChild = el.firstElementChild as HTMLElement | null;
      if (firstChild) {
        firstChild.style.paddingTop = '0';
        firstChild.style.paddingTop = ''; // remove only the inline override, let CSS win
      }
    });
    return (
      <div
        ref={ref}
        className="mobile-scroll"
        data-ggj-native-scroll=""
        style={{ flex: 1, minHeight: 0, overflowY: 'auto', WebkitOverflowScrolling: 'touch' as React.CSSProperties['WebkitOverflowScrolling'] }}
      >
        {children}
      </div>
    );
  };

  /**
   * Layered content card — directly under header with -12px overlap, rounded top,
   * theme-aware background (light: page bg, dark: #252528 + top border). Mockup-aligned.
   */
  const LayeredCard: React.FC<{ children: React.ReactNode }> = ({ children }) => (
    <div
      style={{
        marginTop: -12,
        borderRadius: '24px 24px 0 0',
        background: 'var(--ggj-mobile-content-card-bg, var(--vscode-bg-primary))',
        borderTop: 'var(--ggj-mobile-content-card-border-top, none)',
        boxShadow: 'var(--ggj-mobile-content-card-shadow, none)',
        flex: 1,
        minHeight: 0,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
      }}
    >
      {children}
    </div>
  );

  /**
   * nativeWrap — wraps a feature screen with a MobileNativeHeader + layered card + NativeScreenContent.
   * On web it returns the content unchanged.
   */
  const nativeWrap = (content: React.ReactNode, page: DashboardPage) => {
    if (!isNative) return content;
    const titleKey = pageTitleKeys[page];
    const title = titleKey ? t(titleKey, SCREEN_LABELS[page]) : SCREEN_LABELS[page];
    return (
      <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
        <MobileNativeHeader
          title={title}
          showBack
          onBack={handleBack}
        />
        <LayeredCard>
          <NativeScreenContent>
            {content}
          </NativeScreenContent>
        </LayeredCard>
      </div>
    );
  };

  // Render current page content
  const renderPageContent = () => {
    switch (currentPage) {
      case 'dashboard':
        return isNativePlatform()
          ? <MobileHomeDashboard onNavigate={handleNavigate} />
          : (
            <div className="flex-1 min-h-0 h-full overflow-hidden flex flex-col">
              <DashboardOverview onNavigate={handleNavigate} />
            </div>
          );

      case 'journey-setup':
        return nativeWrap(<JourneySetupPage onNavigate={handleNavigate} />, 'journey-setup');

      case 'single-optimize': {
        // ── DEPRECATED ROUTE ─────────────────────────────────────────────
        // The single-optimize page was retired when optimization moved to
        // the AI Agent. Any legacy navigation (sessionStorage state,
        // deep-link, browser back/forward) lands here and is immediately
        // redirected to /agent. The page string is kept in the
        // DashboardPage type purely so old callers compile; new code
        // MUST navigate to 'agent' instead. The original component lives
        // in src/_deadcode/SingleOptimizeFlow/ for reference.
        setTimeout(() => handleNavigate('agent'), 0);
        return (
          <div className="flex-1 min-h-0 flex items-center justify-center">
            <LoadingSpinner size="md" />
          </div>
        );
      }

      case 'history':
        return nativeWrap(
          <div data-tour="page-history" style={{ height: '100%', display: 'flex', flexDirection: 'column', minHeight: 0, overflow: 'hidden' }}>
            <OptimizationHistory onNavigate={handleNavigate} />
          </div>,
          'history',
        );

      case 'resume-builder': {
        const templateId = typeof sessionStorage !== 'undefined' ? sessionStorage.getItem('resume_builder_template') : null;
        return nativeWrap(
          <div className="h-full min-h-0 flex flex-col overflow-hidden">
            <ResumeBuilderView
              onBack={() => handleNavigate('history')}
              onNavigate={(page) => handleNavigate(page as DashboardPage)}
              initialTemplateId={templateId || undefined}
              onDownload={(format) => { console.log('[ResumeBuilder] Download requested:', format); }}
            />
          </div>,
          'resume-builder',
        );
      }

      case 'payment-history':
        return nativeWrap(<PaymentHistoryPage />, 'payment-history');

      case 'my-activities':
        return nativeWrap(<MyActivities onNavigate={handleNavigate} />, 'my-activities');

      case 'prepare-for-interview':
        return nativeWrap(<PrepareForInterview onNavigate={handleNavigate} />, 'prepare-for-interview');

      case 'jobs':
        return nativeWrap(<Jobs onNavigate={handleNavigate} mode="recommended" />, 'jobs');

      case 'jobs-admin':
        return nativeWrap(<Jobs onNavigate={handleNavigate} mode="admin" />, 'jobs-admin');

      case 'objectives-tasks':
        return nativeWrap(<ObjectivesTasksPage />, 'objectives-tasks');

      case 'collaborate':
        return nativeWrap(<Collaborate />, 'collaborate');

      case 'ai-companion':
      case 'calendar-notes':
        return nativeWrap(<DashboardOverview />, 'dashboard');

      case 'agent':
        return (
          <div className="flex-1 min-h-0 flex flex-col overflow-hidden">
            <AgentHubPage onSignOut={onSignOut} />
          </div>
        );

      case 'support':
        return nativeWrap(<SupportTickets />, 'support');

      case 'product-feedback':
        return nativeWrap(<ProductFeedbackPage />, 'product-feedback');

      case 'profile':
        return isNative
          ? <MobileProfileMenu onSignOut={onSignOut} onNavigate={handleNavigate} />
          : <AccountSettingsPage onNavigate={handleNavigate} initialTab="profile" />;

      case 'profile-info':
        return nativeWrap(<UserProfile />, 'profile-info');

      case 'credits':
        return nativeWrap(<Subscription />, 'credits');

      case 'developer':
        return nativeWrap(
          <AccountSettingsPage onNavigate={handleNavigate} initialTab="api-keys" />,
          'developer',
        );

      case 'account-settings':
        return nativeWrap(
          <AccountSettingsPage onNavigate={handleNavigate} />,
          'account-settings',
        );

      case 'skill-assessment':
        return nativeWrap(
          <SkillAssessment onClose={() => handleNavigate('profile')} />,
          'skill-assessment',
        );

      case 'blog-cms':
        return nativeWrap(
          <AccountSettingsPage onNavigate={handleNavigate} initialTab="blog-cms" />,
          'account-settings',
        );

      case 'blog-editor': {
        const p = window.location.pathname;
        if (p === '/admin/blogs/new') return <BlogEditorPage embedded />;
        const m = p.match(/^\/admin\/blogs\/([^/]+)\/edit$/);
        if (m) return <BlogEditorPage embedded postId={m[1]} />;
        return <BlogListPage embedded />;
      }

      case 'careers-cms':
        return <CareersListPage embedded />;

      case 'careers-editor': {
        const p = window.location.pathname;
        if (p === '/dashboard/careers/new') return <CareersEditorPage embedded />;
        const cm = p.match(/^\/dashboard\/careers\/([^/]+)\/edit$/);
        if (cm) return <CareersEditorPage embedded postId={cm[1]} />;
        return <CareersListPage embedded />;
      }

      default:
        return <DashboardOverview onNavigate={handleNavigate} />;
    }
  };

  return (
    <TourProvider currentPage={currentPage} onNavigate={handleNavigate as (page: string) => void}>
      {/* ── Native Android: NO web Layout chrome (no hamburger, no header bar) ── */}
      {isNative ? (
        <div
          style={{ height: '100dvh', background: 'var(--vscode-bg-primary)', overflow: 'hidden' }}
        >
          <MobileLayout
            currentPage={currentPage}
            onNavigate={handleNavigate}
            onTabChange={() => setNavStack([])}
            hideTabBar={currentPage === 'single-optimize'}
          >
            {/* Position container so absolute-positioned skeleton is contained */}
            <div style={{ position: 'relative', height: '100%' }}>
              <AnimatePresence mode="wait">
                <motion.div
                  key={currentPage}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.15 }}
                  style={{ height: '100%' }}
                >
                  {renderPageContent()}
                </motion.div>
              </AnimatePresence>

              {/* Skeleton overlay — covers page content briefly during transitions */}
              <AnimatePresence>
                {showSkeleton && (
                  <motion.div
                    key="skeleton"
                    initial={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.15 }}
                    style={{ position: 'absolute', inset: 0, zIndex: 50 }}
                  >
                    <MobilePageSkeleton />
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </MobileLayout>
        </div>
      ) : (
      /* ── Web / Desktop: full Layout with sidebar ── */
      <Layout
        currentPage={currentPage as DashboardPage}
        onNavigate={handleNavigate}
        onSignOut={onSignOut}
        onHeroSearch={handleHeroSearch}
      >
        <MobileLayout currentPage={currentPage} onNavigate={handleNavigate}>
        <AnimatePresence mode="wait">
          <motion.div
            key={currentPage}
            variants={slideTransitionVariants}
            initial="initial"
            animate="in"
            exit="out"
            custom={1}
            className="flex-1 min-h-0 relative z-0 flex flex-col"
            style={{ pointerEvents: 'auto' }}
          >
            {renderPageContent()}
          </motion.div>
        </AnimatePresence>
        </MobileLayout>
      </Layout>
      )}

      {/* Onboarding Modal */}
      <AnimatePresence>
        {showOnboarding && (
          <motion.div
            className="fixed inset-0 z-[9999] vscode-bg-primary"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <OnboardingFlow
              onComplete={handleOnboardingComplete}
              onSkip={handleOnboardingComplete}
              onCancel={handleOnboardingComplete}
            />
          </motion.div>
        )}
      </AnimatePresence>

      {/* Session Timeout Modal */}
      <SessionTimeoutModal
        isVisible={isWarningVisible}
        timeRemaining={timeRemaining}
        onExtendSession={extendSession}
        onSignOut={signOutNow}
      />

      {/* WO-17 PR-C: Apply-confirmation bottom-sheet */}
      <ApplyConfirmationModal
        open={pendingApply !== null}
        job={pendingApply?.job ?? null}
        clickId={pendingApply?.clickId ?? null}
        onClose={handleModalClose}
      />
    </TourProvider>
  );
};

export default DashboardContainer;
